import React, { useState, useRef, useEffect } from 'react';
import { FaCloudUploadAlt, FaTimes, FaFile, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import styles from './DocumentUpload.module.css';
import { clientDocumentLinkService } from '../../services/clientDocumentLinkService';
import { useAccessToken } from '../../hooks';
import { useAlert } from '../../context/AlertContext';

/**
 * Document Upload Component
 * Allows users to upload documents via browse or drag-and-drop
 * Automatically creates client-document links
 * Prevents uploading duplicate files
 */
const DocumentUpload = ({ clientId, container = 'client', existingDocuments = [], onUploadSuccess, onClose }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { setInfoAlert, setErrorAlert } = useAlert();

    const [dragActive, setDragActive] = useState(false);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const fileInputRef = useRef(null);

    // Allowed file types
    const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png',
        'image/gif',
        'text/plain',
        'text/csv'
    ];

    // Maximum file size (50MB)
    const maxFileSize = 50 * 1024 * 1024;

    /**
     * Handle drag events
     */
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    /**
     * Handle drop event
     */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    /**
     * Handle file selection
     */
    const handleFileInput = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    /**
     * Process and validate selected files
     */
    const handleFiles = (fileList) => {
        const newFiles = Array.from(fileList).map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'pending', // pending, uploading, success, error
            error: null,
            progress: 0
        }));

        // Validate files
        const validatedFiles = newFiles.map(fileObj => {
            // Check if file already exists
            const isDuplicate = existingDocuments.some(doc => 
                doc.fileName === fileObj.name && doc.container === container
            );
            
            if (isDuplicate) {
                return {
                    ...fileObj,
                    status: 'error',
                    error: 'File with this name already exists'
                };
            }

            // Check file size
            if (fileObj.size > maxFileSize) {
                return {
                    ...fileObj,
                    status: 'error',
                    error: `File size exceeds ${maxFileSize / 1024 / 1024}MB limit`
                };
            }

            // Check file type
            if (!allowedTypes.includes(fileObj.type) && fileObj.type !== '') {
                return {
                    ...fileObj,
                    status: 'error',
                    error: 'File type not allowed'
                };
            }

            return fileObj;
        });

        // Show alert if any duplicates were found
        const duplicateCount = validatedFiles.filter(f => 
            f.error === 'File with this name already exists'
        ).length;
        
        if (duplicateCount > 0) {
            setErrorAlert(
                `${duplicateCount} file(s) already exist with the same name and will not be uploaded`
            );
        }

        setFiles(prev => [...prev, ...validatedFiles]);
    };

    /**
     * Remove a file from the list
     */
    const removeFile = (fileId) => {
        setFiles(prev => prev.filter(f => f.id !== fileId));
    };

    /**
     * Upload a single file
     */
    const uploadFile = async (fileObj) => {
        try {
            // Update status to uploading
            setFiles(prev => prev.map(f => 
                f.id === fileObj.id ? { ...f, status: 'uploading', progress: 0 } : f
            ));

            const accessToken = await getBackendAccessToken();
            
            // Create virtual directory path with client ID
            // Format: clientId/filename (e.g., "123/document.pdf")
            const virtualPath = `${clientId}/${fileObj.file.name}`;
            
            // Upload and assign document to client with virtual directory path
            const result = await clientDocumentLinkService.uploadAndAssignDocument(
                accessToken,
                fileObj.file,
                clientId,
                container,
                virtualPath // Use clientId/filename format for storage
            );

            // Check if assignment was successful
            if (result.assignmentError) {
                console.warn('Document uploaded but assignment failed:', result.assignmentError);
                setErrorAlert(`Document uploaded but failed to link to client: ${result.assignmentError}`);
            }

            // Update status to success
            setFiles(prev => prev.map(f => 
                f.id === fileObj.id ? { ...f, status: 'success', progress: 100 } : f
            ));

            return result;
        } catch (error) {
            console.error('Upload failed:', error);
            
            // Detect network/CORS errors common on mobile
            let errorMessage = error.message || 'Upload failed';
            if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
                errorMessage = 'Network error. Please check your connection and ensure the app is accessed via HTTPS.';
            } else if (error.message?.includes('CORS')) {
                errorMessage = 'Connection blocked. Please contact support.';
            }
            
            // Update status to error
            setFiles(prev => prev.map(f => 
                f.id === fileObj.id ? { 
                    ...f, 
                    status: 'error', 
                    error: errorMessage
                } : f
            ));

            throw error;
        }
    };

    /**
     * Upload all pending files
     */
    const handleUploadAll = async () => {
        const pendingFiles = files.filter(f => f.status === 'pending');
        
        if (pendingFiles.length === 0) {
            setErrorAlert('No files to upload');
            return;
        }

        setUploading(true);

        try {
            // Upload files sequentially
            const results = [];
            for (const fileObj of pendingFiles) {
                try {
                    const result = await uploadFile(fileObj);
                    results.push(result);
                } catch (error) {
                    console.error(`Failed to upload ${fileObj.name}:`, error);
                }
            }

            // Count successful uploads from the results array, not from state
            const successCount = results.filter(r => r && r.success).length;
            const errorCount = pendingFiles.length - successCount;

            if (successCount > 0) {
                setInfoAlert(`Successfully uploaded ${successCount} file(s)`);
                
                // Call success callback and wait for it to complete
                if (onUploadSuccess) {
                    await onUploadSuccess(results);
                }
            }

            if (errorCount > 0) {
                setErrorAlert(`Failed to upload ${errorCount} file(s)`);
            }

            // Clear successful uploads after callback completes
            setTimeout(() => {
                setFiles(prev => prev.filter(f => f.status !== 'success'));
            }, 3000);

        } catch (error) {
            console.error('Upload process failed:', error);
            setErrorAlert('Upload process failed');
        } finally {
            setUploading(false);
        }
    };

    /**
     * Format file size for display
     */
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    /**
     * Get status icon
     */
    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <FaCheckCircle className={styles.iconSuccess} />;
            case 'error':
                return <FaExclamationCircle className={styles.iconError} />;
            case 'uploading':
                return <div className={styles.spinner}></div>;
            default:
                return <FaFile className={styles.iconPending} />;
        }
    };

    const pendingCount = files.filter(f => f.status === 'pending').length;
    const successCount = files.filter(f => f.status === 'success').length;
    const errorCount = files.filter(f => f.status === 'error').length;

    return (
        <div className={styles.uploadContainer}>
            {/* Header */}
            <div className={styles.header}>
                <h3>Upload Documents</h3>
                {onClose && (
                    <button className={styles.closeButton} onClick={onClose}>
                        <FaTimes />
                    </button>
                )}
            </div>

            {/* Drop Zone */}
            <div
                className={`${styles.dropZone} ${dragActive ? styles.dragActive : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <FaCloudUploadAlt className={styles.uploadIcon} />
                <p className={styles.dropText}>
                    Drag and drop files here or <span className={styles.browseText}>browse</span>
                </p>
                <p className={styles.dropHint}>
                    Supported: PDF, Word, Excel, Images, Text files (Max {maxFileSize / 1024 / 1024}MB)
                </p>
                <p className={styles.maxSizeNote}>
                    Note: Maximum file size is 50MB
                </p>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileInput}
                    className={styles.fileInput}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,image/*,application/pdf"
                    capture="environment"
                />
            </div>

            {/* File List */}
            {files.length > 0 && (
                <div className={styles.fileList}>
                    <div className={styles.fileListHeader}>
                        <h4>Files ({files.length})</h4>
                        <div className={styles.stats}>
                            {pendingCount > 0 && <span className={styles.statPending}>{pendingCount} pending</span>}
                            {successCount > 0 && <span className={styles.statSuccess}>{successCount} uploaded</span>}
                            {errorCount > 0 && <span className={styles.statError}>{errorCount} failed</span>}
                        </div>
                    </div>

                    <div className={styles.fileItems}>
                        {files.map(fileObj => (
                            <div key={fileObj.id} className={`${styles.fileItem} ${styles[fileObj.status]}`}>
                                <div className={styles.fileIcon}>
                                    {getStatusIcon(fileObj.status)}
                                </div>
                                
                                <div className={styles.fileInfo}>
                                    <div className={styles.fileName}>{fileObj.name}</div>
                                    <div className={styles.fileSize}>{formatFileSize(fileObj.size)}</div>
                                    {fileObj.error && (
                                        <div className={styles.fileError}>{fileObj.error}</div>
                                    )}
                                </div>

                                {fileObj.status === 'pending' && (
                                    <button
                                        className={styles.removeButton}
                                        onClick={() => removeFile(fileObj.id)}
                                        title="Remove"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Actions */}
            {files.length > 0 && (
                <div className={styles.actions}>
                    <button
                        className={styles.clearButton}
                        onClick={() => setFiles([])}
                        disabled={uploading}
                    >
                        Clear All
                    </button>
                    <button
                        className={styles.uploadButton}
                        onClick={handleUploadAll}
                        disabled={uploading || pendingCount === 0}
                    >
                        {uploading ? 'Uploading...' : `Upload ${pendingCount} File(s)`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default DocumentUpload;
